import { observer } from "mobx-react-lite"
import * as yup from "yup"
import styles from "../sharedStyles.module.scss"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import { FormSelectCreateableInput } from "../../../../atoms/FormSelectCreateableInput"
import { useMutation, useQuery } from "@tanstack/react-query"
import {
  getHostAgencies,
  getAgencyConsortia,
  upsertHostAgency,
} from "../../../../../services/firebase-functions/dictionaries"
import { upsertAgencyConsortia } from "../../../../../services/firebase-functions/dictionaries"
import { useQueryClient } from "@tanstack/react-query"
import { FormPhoneInput } from "../../../../atoms/FormPhoneInput"
import { isPhoneValid } from "../../../../../utils/validation"
import FormTextInput from "../../../../atoms/FormTextInput"
import { colors } from "@theme/colors"

type AgencyInformationForm = {
  agencyName: string
  agencyPhoneNumber: string
  hostAgencyId: string
  consortiaId: string
}

const agencyInformationRequiredSchema = yup.object().shape({
  agencyName: yup.string(),
  agencyPhoneNumber: yup.string(),
  hostAgencyId: yup.string(),
  consortiaId: yup.string(),
})

export const AgencyInformation = observer(
  ({
    onSubmit,
    goBack,
    formValues,
  }: {
    onSubmit: (data: AgencyInformationForm) => void
    goBack: (data: AgencyInformationForm) => void
    formValues: AgencyInformationForm
  }) => {
    const queryClient = useQueryClient()

    const { data: hostAgencies, isLoading: isLoadingHostAgencies } = useQuery({
      queryKey: ["hostAgencies"],
      queryFn: () => getHostAgencies(),
    })

    const { data: consortia, isLoading: isLoadingConsortia } = useQuery({
      queryKey: ["consortia"],
      queryFn: () => getAgencyConsortia(),
    })

    const createHostAgencyOptionMutation = useMutation({
      mutationFn: async (hostAgencyName: string) => {
        const newHostAgency = await upsertHostAgency({ hostAgencyName })

        queryClient
          .invalidateQueries({ queryKey: ["hostAgencies"] })
          .catch((error) => console.error("Error invalidating host agencies query", error))

        return {
          value: String(newHostAgency.id),
          label: newHostAgency.hostAgencyName,
        }
      },
    })

    const createConsortiaOptionMutation = useMutation({
      mutationFn: async (consortiaName: string) => {
        const newConsortia = await upsertAgencyConsortia({ consortiaName })

        queryClient
          .invalidateQueries({ queryKey: ["consortia"] })
          .catch((error) => console.error("Error invalidating consortia query", error))

        return {
          value: String(newConsortia.id),
          label: newConsortia.consortiaName,
        }
      },
    })

    const {
      control,
      handleSubmit,
      watch,
      getValues,
      formState: { isValid },
    } = useForm({
      defaultValues: {
        agencyName: formValues.agencyName,
        agencyPhoneNumber: formValues.agencyPhoneNumber,
        hostAgencyId: formValues.hostAgencyId,
        consortiaId: formValues.consortiaId,
      },
      resolver: yupResolver(agencyInformationRequiredSchema),
    })

    const [agencyPhoneNumber] = watch(["agencyPhoneNumber"])
    const isAgencyPhoneNumberValid = isPhoneValid(agencyPhoneNumber)

    const handleSubmitAgencyInformation = (data: AgencyInformationForm) => {
      const newUserPayload = {
        ...data,
        agencyPhoneNumber: isAgencyPhoneNumberValid ? data.agencyPhoneNumber : "",
      }

      onSubmit(newUserPayload)
    }

    const sortedHostAgencies =
      hostAgencies
        ?.map((hostAgency) => ({
          value: String(hostAgency.id),
          label: hostAgency.hostAgencyName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    const sortedConsortia =
      consortia
        ?.map((consortia) => ({
          value: String(consortia.id),
          label: consortia.consortiaName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    return (
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(handleSubmitAgencyInformation)()
        }}
      >
        <div className={styles.inputsContainer}>
          <FormTextInput
            control={control}
            autoFocus
            name="agencyName"
            label="Agency name"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormPhoneInput
            control={control}
            name="agencyPhoneNumber"
            label="Phone number"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormSelectCreateableInput
            control={control}
            name="hostAgencyId"
            options={sortedHostAgencies}
            label="Host agency"
            createOptionMutation={createHostAgencyOptionMutation}
            isDisabled={isLoadingHostAgencies}
            parentBackgroundColor={
              isLoadingHostAgencies ? colors.backgroundTertiary : colors.backgroundSecondary
            }
          />
          <FormSelectCreateableInput
            control={control}
            name="consortiaId"
            options={sortedConsortia}
            label="Consortia"
            createOptionMutation={createConsortiaOptionMutation}
            isDisabled={isLoadingConsortia}
            parentBackgroundColor={
              isLoadingConsortia ? colors.backgroundTertiary : colors.backgroundSecondary
            }
          />
        </div>
        <button
          type="submit"
          className={styles.button}
          disabled={
            !isValid ||
            (agencyPhoneNumber && agencyPhoneNumber !== "+1" && !isAgencyPhoneNumberValid)
          }
          data-testid="go-to-agency-survey-button"
        >
          Next
        </button>
        <button
          type="button"
          className={styles.previousButton}
          onClick={() => {
            const values = getValues()
            goBack({
              agencyName: values?.agencyName || "",
              agencyPhoneNumber: values?.agencyPhoneNumber || "",
              hostAgencyId: values?.hostAgencyId || "",
              consortiaId: values?.consortiaId || "",
            })
          }}
        >
          <ArrowBackOutline className={styles.previousIcon} /> Previous page
        </button>
      </form>
    )
  },
)
